import { __decorate } from 'tslib';
import { coerceNumberProperty, coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { InjectionToken, Directive, HostBinding, EventEmitter, isDevMode, PLATFORM_ID, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, ContentChildren, ViewChild, Input, Output, HostListener, Injectable, NgModule } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, interval } from 'rxjs';
import { startWith, filter, takeWhile, distinctUntilChanged } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { HammerGestureConfig, HammerModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';

const GPU_CAROUSEL = new InjectionToken('GpuCarouselComponent');

class GpuCarouselItemDirective {
    get isActiveClass() {
        return this.isActive;
    }
    get getRightGap() {
        return this.rightGap;
    }
    get getItemWidth() {
        return this.itemWidth;
    }
    get getTransitionDuration() {
        return this.transitionDuration;
    }
    get getTransition() {
        if (this.transitionDestination !== null) {
            return `transform: translate3d(${this.transitionDestination}px, 0px, 0px)`;
        }
        return '';
    }
    get getContinuousData() {
        if (!this.continuousData) {
            return '';
        }
        return `
      --delay: -${this.continuousData.delay / 1000}s;
      --duration: ${this.continuousData.duration / 1000}s;
      --start-offset: ${this.continuousData.startOffset}px;
      --end-offset: ${this.continuousData.endOffset}px;
    `;
    }
    get ariaLabel() {
        return this._ariaLabel;
    }
    set ariaLabel(label) {
        if (this._ariaLabel === label) {
            return;
        }
        setTimeout(() => {
            this._ariaLabel = label;
            this.cdRef.markForCheck();
        });
    }
    constructor(injector, cdRef, element) {
        this.injector = injector;
        this.cdRef = cdRef;
        this.element = element;
        this.carouselItem = true;
        this.carouselItemAnimation = false;
        this.isActive = false;
        this.rightGap = 0;
        this.itemWidth = 'max-content';
        this.transitionDestination = null;
        this.transitionDuration = 400;
        this.continuousData = null;
        this._ariaLabel = null;
        this.transitionDuration = this.carousel?.transitionDuration || 400;
        this.carousel = this.injector.get(GPU_CAROUSEL);
        this.setItemWidth(this.carousel.getItemWidth());
        this.setRightGap(this.carousel.itemGap);
    }
    setActive(active) {
        setTimeout(() => {
            this.isActive = active;
            this.cdRef.markForCheck();
        });
    }
    setRightGap(gap) {
        if (this.rightGap === gap) {
            return;
        }
        this.rightGap = gap;
        this.cdRef.markForCheck();
    }
    setItemWidth(width) {
        if (this.itemWidth === width) {
            return;
        }
        this.itemWidth = width;
        this.cdRef.markForCheck();
    }
    setItemTransition(destination) {
        this.transitionDestination = destination;
        this.cdRef.markForCheck();
    }
    setTransitionDuration(duration) {
        this.transitionDuration = duration;
        this.cdRef.markForCheck();
    }
    setContinuousData(data) {
        this.continuousData = data;
        this.carouselItemAnimation = true;
        this.cdRef.markForCheck();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselItemDirective, deps: [{ token: i0.Injector }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: GpuCarouselItemDirective, selector: "[gpuCarouselItem]", host: { properties: { "class.gpu-carousel-item": "this.carouselItem", "class.gpu-carousel-item-animation": "this.carouselItemAnimation", "class.gpu-carousel-active": "this.isActiveClass", "style.margin-right.px": "this.getRightGap", "style.width": "this.getItemWidth", "style.transition-duration.ms": "this.getTransitionDuration", "style": "this.getContinuousData", "attr.aria-label": "this.ariaLabel" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[gpuCarouselItem]',
                }]
        }], ctorParameters: () => [{ type: i0.Injector }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }], propDecorators: { carouselItem: [{
                type: HostBinding,
                args: ['class.gpu-carousel-item']
            }], carouselItemAnimation: [{
                type: HostBinding,
                args: ['class.gpu-carousel-item-animation']
            }], isActiveClass: [{
                type: HostBinding,
                args: ['class.gpu-carousel-active']
            }], getRightGap: [{
                type: HostBinding,
                args: ['style.margin-right.px']
            }], getItemWidth: [{
                type: HostBinding,
                args: ['style.width']
            }], getTransitionDuration: [{
                type: HostBinding,
                args: ['style.transition-duration.ms']
            }], getTransition: [{
                type: HostBinding,
                args: ['style']
            }], getContinuousData: [{
                type: HostBinding,
                args: ['style']
            }], ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }] } });

var DisplayMode;
(function (DisplayMode) {
    DisplayMode["VISIBLE_ITEMS"] = "visible-items";
    DisplayMode["FIXED_WIDTH"] = "fixed-width";
})(DisplayMode || (DisplayMode = {}));

var Transition;
(function (Transition) {
    Transition["SLIDE"] = "slide";
    Transition["FADE"] = "fade";
})(Transition || (Transition = {}));

/* eslint-disable no-prototype-builtins */
const verifySettings = (component) => {
    if (!window.hasOwnProperty("Hammer")) {
        console.warn("You need hammerjs for ngx-carousel swipe to work!");
    }
    if (component.transition === Transition.FADE &&
        (component.displayMode !== DisplayMode.VISIBLE_ITEMS || component.pageSize !== 1)) {
        console.error(`
    [ngx-ui/carousel] Invalid config #1; Fade transition only allowed with single item view.\n
    To use Transition.FADE you must use DisplayMode.VISIBLE_ITEMS and pageSize = 1
    `);
    }
    if (component.displayMode === DisplayMode.FIXED_WIDTH && component.pageSize !== 1) {
        console.error(`
    [ngx-ui/carousel] Invalid config #2; Cannot use fixed item width and number or visible items!\n
    To use DisplayMode.FIXED_WIDTH you should not change pageSize.
    `);
    }
    if (component.displayMode === DisplayMode.VISIBLE_ITEMS && component.transitionAll && component.pageSize === 1) {
        console.warn(`
    [ngx-ui/carousel] Invalid config #3; TransitionAll has no effect when only one item is visible!\n
    To use DisplayMode.VISIBLE_ITEMS with transitionAll, pageSize must be 2 or more.
    `);
    }
    if (component.displayMode === DisplayMode.FIXED_WIDTH && component.transitionAll) {
        console.error(`
    [ngx-ui/carousel] Invalid config #4; TransitionAll doesn't work with fixed width items!\n
    To use DisplayMode.FIXED_WIDTH do not use transitionAll.
    `);
    }
    // if (component.infinite && (component.displayMode !== DisplayMode.VISIBLE_ITEMS || component.pageSize !== 1) && !component.autoplayContinuous) {
    //   console.error(`
    //   [ngx-ui/carousel] Invalid config #5; Infinite currently supported for single item or continuous autoplay!\n
    //   `);
    // }
};

let GpuCarouselComponent = class GpuCarouselComponent {
    get swipeActive() {
        return this.isPanning;
    }
    get getHideOverflow() {
        return this.transition === Transition.FADE || (this.infinite && this.autoplayContinuous);
    }
    /**
     * How long animation should be between two slides
     * Duration is in ms
     */
    get transitionDuration() {
        // When panning we should remove animation to follow mouse cursor
        return this.isPanning ? 0 : this._transitionDuration;
    }
    set transitionDuration(value) {
        const newValue = coerceNumberProperty(value);
        this.updateIfChanged('_transitionDuration', newValue);
    }
    // Gap between items, in px.
    get itemGap() {
        return this._itemGap;
    }
    set itemGap(value) {
        const newValue = coerceNumberProperty(value);
        this.updateIfChanged('_itemGap', newValue);
    }
    get swipeEnabled() {
        return this._swipeEnabled;
    }
    set swipeEnabled(value) {
        const newValue = coerceBooleanProperty(value);
        this.updateIfChanged('_swipeEnabled', newValue);
    }
    get tapEnabled() {
        return this._tapEnabled;
    }
    set tapEnabled(value) {
        this.updateIfChanged('_tapEnabled', coerceBooleanProperty(value));
    }
    /**
     * Number of concurrently visible items, defaults to 1.
     * If DisplayMode.FIXED_WIDTH is used this will be ignored.
     */
    get pageSize() {
        return this._pageSize;
    }
    set pageSize(value) {
        const newValue = coerceNumberProperty(value);
        this.updateIfChanged('_pageSize', newValue);
    }
    /**
     * For DisplayMode.VISIBLE_ITEMS you can define if all visible items (page) will transition.
     */
    get transitionAll() {
        return this._transitionAll;
    }
    set transitionAll(value) {
        const newValue = coerceBooleanProperty(value);
        this.updateIfChanged('_transitionAll', newValue);
    }
    constructor(platformId, cdRef, renderer) {
        this.platformId = platformId;
        this.cdRef = cdRef;
        this.renderer = renderer;
        this.class = 'gpu-carousel';
        // If there is infinite scroll - goes around
        this.infinite = false;
        // If animation starts automatically.
        this.autoplay = false;
        // If autoplay should be continuous - without stopping at each page
        this.autoplayContinuous = false;
        // How long it should stay on item, before playing on.
        this.autoplayInterval = 4000;
        this.displayMode = DisplayMode.VISIBLE_ITEMS;
        this.transition = Transition.SLIDE;
        this.pageChange = new EventEmitter();
        this.DisplayMode = DisplayMode;
        this.activePage$ = new BehaviorSubject(0);
        this.pageCount$ = new BehaviorSubject(0);
        /**
         * If user changed page - interrupt autoplay.
         */
        this.userInterrupted = false;
        this._transitionAll = false;
        this._pageSize = 1;
        this._swipeEnabled = true;
        this._tapEnabled = false;
        this._transitionDuration = 400;
        this._itemGap = 32;
        this.isPanning = false;
    }
    ngOnInit() {
        // Verify settings
        // Not all settings values can be used together, throw error when incorrect combination is used.
        if (isPlatformBrowser(this.platformId)) {
            if (isDevMode()) {
                verifySettings(this);
            }
            this.renderer.listen(window, 'resize', () => {
                this.onResize();
            });
        }
    }
    ngAfterContentInit() {
        // TODO: Change which page is active
        const defaultActive = 0;
        // If data is loaded async, items won't be available yet.
        this.items.changes
            .pipe(untilDestroyed(this), startWith(0), filter(() => !!this.items.length))
            .subscribe(() => {
            this.onResize();
            this.setPageCount();
            this.setActivePage(defaultActive);
            this.setAriaLabel();
        });
    }
    ngAfterViewInit() {
        this.onResize();
        if (this.autoplay) {
            this.startAutoPlay();
        }
        if (this.autoplayContinuous) {
            this.startContinuousAutoplay();
        }
    }
    next() {
        if (this.activePage < this.pageCount - 1 || this.infinite) {
            this.setActivePage((this.activePage + 1) % this.pageCount);
        }
    }
    prev() {
        if (this.activePage > 0 || this.infinite) {
            this.setActivePage((this.activePage - 1 + this.pageCount) % this.pageCount);
        }
    }
    onSwipeLeft() {
        if (this.swipeEnabled && this.transition === Transition.FADE) {
            document.getSelection().removeAllRanges();
            this.userInterrupted = true;
            this.next();
        }
    }
    onSwipeRight() {
        if (this.swipeEnabled && this.transition === Transition.FADE) {
            document.getSelection().removeAllRanges();
            this.userInterrupted = true;
            this.prev();
        }
    }
    /**
     * On pan. While user is panning we follow it with slider. Once panning stops we calculate which page he stopped on and snap to it.
     * @param event Hammerjs pan event
     */
    onPan(event) {
        if (!this.swipeEnabled || this.transition !== Transition.SLIDE || this.autoplayContinuous) {
            return;
        }
        document.getSelection().removeAllRanges();
        this.userInterrupted = true;
        // We need isPanning to set animation to 0.
        if (!this.isPanning) {
            this.isPanning = true;
        }
        // This could be extracted elsewhere?
        const offset = (this.firstItemWidth() + this.itemGap) * this.activePage * this.transitionSize() - event.deltaX;
        this.renderer.setStyle(this.slider.nativeElement, 'transform', `translate3d(${-offset}px, 0px, 0px)`);
        // Animate to most correct page when panning is over
        if (event.isFinal) {
            this.isPanning = false;
            let velocityOffset = 0;
            if (Math.abs(event.velocityX) > 0.05) {
                /**
                 * Use pan velocity
                 * 0.2 is used to reduce effect
                 * We multiply by 1000 to get px/s, since Hammerjs velocityX is in px/ms
                 * After that we revert direction.
                 */
                velocityOffset = event.velocityX * 0.2 * 1000 * -1;
            }
            /**
             * Calculate transition width, which is the same as page width when `transitionAll` is enabled.
             */
            const transitionWidth = (this.firstItemWidth() + this.itemGap) * (this.transitionAll ? this.pageSize : 1);
            /**
             * Calculate which page it stopped on. If more then half an item is already hidden, use the next one.
             * We need to Math.min and Math.max to avoid overflowing/going into negatives.
             */
            const page = Math.round((offset + velocityOffset) / transitionWidth);
            const fixedPage = Math.max(Math.min(page, this.pageCount - 1), 0);
            this.setPage(fixedPage);
        }
    }
    onTap(event) {
        if (this.tapEnabled) {
            this.userInterrupted = true;
            if (this.pageSize === 1) {
                const offsetFactor = 0.3;
                if (this.sliderWidth * offsetFactor > event.srcEvent.layerX) {
                    this.prev();
                }
                else if (this.sliderWidth * (1 - offsetFactor) < event.srcEvent.layerX) {
                    this.next();
                }
            }
            //TODO - tap for page size higher than 1
        }
    }
    isSingleItem() {
        return this.displayMode === DisplayMode.VISIBLE_ITEMS && this.pageSize === 1;
    }
    /**
     * This method is to publicly expose expose changing to certain page.
     * @param index Page number
     */
    setPage(index) {
        this.setActivePage(index);
    }
    getItemWidth() {
        if (this.displayMode === DisplayMode.VISIBLE_ITEMS) {
            const width = 100 / this.pageSize;
            const padding = ((this.pageSize - 1) * this.itemGap) / this.pageSize;
            return `calc(${width}% - ${padding}px)`;
        }
        else {
            return 'max-content';
        }
    }
    startAutoPlay() {
        // TODO: Start autoplay only when component is in view
        interval(this.autoplayInterval)
            .pipe(untilDestroyed(this), takeWhile(() => !this.userInterrupted))
            .subscribe(() => this.next());
    }
    setItemsGap() {
        if (this.transition !== Transition.FADE) {
            this.items.forEach((item) => {
                item.setRightGap(this.itemGap);
            });
        }
    }
    setPageCount() {
        // Block calculation before items are available
        if (!this.items.length) {
            return;
        }
        if (this.displayMode === DisplayMode.FIXED_WIDTH) {
            /**
             * We should count 1 page for every item completely visible, and the rest individually.
             * This way last page will completely uncover last item, without leaving empty space right.
             */
            const visibleItems = Math.floor(this.sliderWidth / (this.firstItemWidth() + this.itemGap));
            const count = this.items.length - visibleItems + 1;
            /**
             * We need to check if count is > 0, because it could be negative.
             * There are situations where items won't render fast enough and have width = 0
             */
            this.pageCount$.next(count > 0 ? count : 0);
        }
        else if (this.transitionAll && this.pageSize > 1) {
            this.pageCount$.next(Math.ceil(this.items.length / this.pageSize));
        }
        else if (this.displayMode === DisplayMode.VISIBLE_ITEMS && this.pageSize > 1) {
            // Make sure last page is always full.
            this.pageCount$.next(this.items.length - (this.pageSize - 1));
        }
        else {
            this.pageCount$.next(this.items.length);
        }
    }
    /**
     * Resize all items. Also used for first load.
     */
    onResize() {
        if (!this.autoplayContinuous) {
            this.setItemsWidth();
            this.setItemsGap();
            // Timeout required to wait for item rendering - otherwise DisplayMode.FIXED_WIDTH could be broken.
            setTimeout(() => {
                this.setPageCount();
                this.moveSlider();
            });
        }
        else {
            this.startContinuousAutoplay();
        }
    }
    /**
     * Activate a different page
     * @param page Index of newly active page
     */
    setActivePage(page) {
        // Unassign current from active
        this.items.get(this.activePage * this.transitionSize()).setActive(false);
        this.activePage$.next(page);
        this.pageChange.emit(page);
        // Make new index active
        this.items.get(page * this.transitionSize()).setActive(true);
        this.onPageChanged();
        this.cdRef.markForCheck();
    }
    /**
     * Return transition size
     * @returns Number of items that should transition
     */
    transitionSize() {
        return this.transitionAll ? this.pageSize : 1;
    }
    /**
     * Actions that should happen once page has changed.
     */
    onPageChanged() {
        if (this.transition === Transition.SLIDE) {
            this.moveSlider();
        }
    }
    moveSlider() {
        // Slider might be available yet
        if (!this.slider) {
            return;
        }
        // Do not slide on FADE!
        if (this.transition !== Transition.SLIDE) {
            return;
        }
        if (this.displayMode === DisplayMode.VISIBLE_ITEMS && this.pageSize === 1) {
            // Move whole slider width
            const offset = (this.sliderWidth + this.itemGap) * this.activePage;
            this.renderer.setStyle(this.slider.nativeElement, 'transform', `translate3d(-${offset}px, 0px, 0px)`);
        }
        if ((this.displayMode === DisplayMode.VISIBLE_ITEMS && this.pageSize > 1) ||
            this.displayMode === DisplayMode.FIXED_WIDTH) {
            // We use width of the first element.
            // TODO: Do not translate whole page if last page is not full!
            const offset = (this.firstItemWidth() + this.itemGap) * this.activePage * this.transitionSize();
            this.renderer.setStyle(this.slider.nativeElement, 'transform', `translate3d(-${offset}px, 0px, 0px)`);
        }
    }
    /**
     * Add transition to items in continuous autoplay.
     */
    startContinuousAutoplay() {
        const itemWithPadding = this.itemGap + this.firstItemWidth();
        const data = {
            startOffset: (this.items.length - 1) * itemWithPadding,
            endOffset: -itemWithPadding,
            duration: this.transitionDuration * this.items.length,
        };
        this.items.forEach((item, index) => {
            item.setContinuousData({ ...data, delay: (this.items.length - 1 - index) * this.transitionDuration });
        });
    }
    setItemsWidth() {
        this.items.forEach((item) => {
            item.setItemWidth(this.getItemWidth());
        });
    }
    firstItemWidth() {
        return this.items.first?.element.nativeElement.clientWidth;
    }
    setAriaLabel() {
        this.items.forEach((item, index) => (item.ariaLabel = `${index + 1} / ${this.items.length}`));
    }
    /**
     * Updates value if changed. Does this:
     * ```ts
     * if (newValue !== this._visibleItems) {
     *   this._visibleItems = newValue;
     *   this.cdRef.markForCheck();
     * }
     * ```
     */
    updateIfChanged(propertyName, value) {
        if (value !== this[propertyName]) {
            this[propertyName] = value;
            this.cdRef.markForCheck();
        }
    }
    get activePage() {
        return this.activePage$.value;
    }
    /**
     * Number of pages. For single visible item pageCount is the same as number of items.
     */
    get pageCount() {
        return this.pageCount$.value;
    }
    get sliderWidth() {
        return this.slider?.nativeElement.clientWidth;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselComponent, deps: [{ token: PLATFORM_ID }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuCarouselComponent, selector: "gpu-carousel", inputs: { infinite: "infinite", autoplay: "autoplay", autoplayContinuous: "autoplayContinuous", autoplayInterval: "autoplayInterval", transitionDuration: "transitionDuration", itemGap: "itemGap", displayMode: "displayMode", swipeEnabled: "swipeEnabled", tapEnabled: "tapEnabled", pageSize: "pageSize", transitionAll: "transitionAll", transition: "transition" }, outputs: { pageChange: "pageChange" }, host: { properties: { "class": "this.class", "class.gpu-carousel-swipe-active": "this.swipeActive", "class.gpu-carousel-overflow-hidden": "this.getHideOverflow" } }, providers: [{ provide: GPU_CAROUSEL, useExisting: GpuCarouselComponent }], queries: [{ propertyName: "items", predicate: GpuCarouselItemDirective, descendants: true }], viewQueries: [{ propertyName: "slider", first: true, predicate: ["slider"], descendants: true }], ngImport: i0, template: "<div\n  class=\"gpu-carousel-wrapper\"\n  (swipeLeft)=\"onSwipeLeft()\"\n  (swipeRight)=\"onSwipeRight()\"\n  (pan)=\"onPan($event)\"\n  (tap)=\"onTap($event)\"\n>\n  <div\n    #slider\n    style=\"user-select: none\"\n    [ngClass]=\"{\n      'gpu-carousel-single': isSingleItem(),\n      'gpu-carousel-fade': transition === 'fade',\n      'gpu-carousel-slide': transition === 'slide',\n      'gpu-carousel-numbered': displayMode === DisplayMode.VISIBLE_ITEMS,\n      'gpu-carousel-fixed-width': displayMode === DisplayMode.FIXED_WIDTH,\n      'gpu-autoplay-continuous': autoplayContinuous\n    }\"\n    [style.transition-duration.ms]=\"transitionDuration\"\n  >\n    <ng-content select=\"[gpuCarouselItem]\"></ng-content>\n  </div>\n</div>\n<ng-content></ng-content>\n", styles: [".gpu-carousel{position:relative;display:flex;flex-direction:column;justify-content:space-between;overflow:hidden}.gpu-carousel-overflow-hidden{overflow:hidden}.gpu-carousel-wrapper{overflow:hidden;margin:-2rem -1.5rem;padding:2rem 1.5rem}.gpu-carousel-item img,img.gpu-carousel-item,.gpu-carousel-swipe-active{pointer-events:none}.gpu-carousel-single.gpu-carousel-fade{display:grid}.gpu-carousel-single.gpu-carousel-fade .gpu-carousel-item{width:100%;opacity:0;transition-property:opacity;transition-timing-function:ease-out;grid-column:1;grid-row:1}.gpu-carousel-single.gpu-carousel-fade .gpu-carousel-item.gpu-carousel-active{opacity:1}.gpu-carousel-single.gpu-carousel-slide{transition-timing-function:ease-out;transition-property:transform;position:relative;margin:0 auto;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.gpu-carousel-single.gpu-carousel-slide .gpu-carousel-item{flex-shrink:0;position:relative;transition-property:transform;width:100%}.gpu-carousel-numbered{transition-timing-function:ease-out;transition-property:transform;position:relative;margin:0 auto;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.gpu-carousel-numbered .gpu-carousel-item{flex-shrink:0;position:relative;transition-property:transform}.gpu-carousel-fixed-width{transition-timing-function:ease-out;transition-property:transform;position:relative;margin:0 auto;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.gpu-carousel-fixed-width .gpu-carousel-item{flex-shrink:0;position:relative;transition-property:transform}.gpu-autoplay-continuous{display:grid}.gpu-autoplay-continuous .gpu-carousel-item{grid-column:1;grid-row:1}.gpu-autoplay-continuous .gpu-carousel-item-animation{animation-duration:var(--duration, 10s);animation-delay:var(--delay, 1s);animation-timing-function:linear;animation-iteration-count:infinite;animation-name:slide}@keyframes slide{0%{transform:translate3d(var(--start-offset, 300px),0,0)}to{transform:translate3d(var(--end-offset, -200px),0,0)}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
GpuCarouselComponent = __decorate([
    UntilDestroy()
], GpuCarouselComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-carousel', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [{ provide: GPU_CAROUSEL, useExisting: GpuCarouselComponent }], template: "<div\n  class=\"gpu-carousel-wrapper\"\n  (swipeLeft)=\"onSwipeLeft()\"\n  (swipeRight)=\"onSwipeRight()\"\n  (pan)=\"onPan($event)\"\n  (tap)=\"onTap($event)\"\n>\n  <div\n    #slider\n    style=\"user-select: none\"\n    [ngClass]=\"{\n      'gpu-carousel-single': isSingleItem(),\n      'gpu-carousel-fade': transition === 'fade',\n      'gpu-carousel-slide': transition === 'slide',\n      'gpu-carousel-numbered': displayMode === DisplayMode.VISIBLE_ITEMS,\n      'gpu-carousel-fixed-width': displayMode === DisplayMode.FIXED_WIDTH,\n      'gpu-autoplay-continuous': autoplayContinuous\n    }\"\n    [style.transition-duration.ms]=\"transitionDuration\"\n  >\n    <ng-content select=\"[gpuCarouselItem]\"></ng-content>\n  </div>\n</div>\n<ng-content></ng-content>\n", styles: [".gpu-carousel{position:relative;display:flex;flex-direction:column;justify-content:space-between;overflow:hidden}.gpu-carousel-overflow-hidden{overflow:hidden}.gpu-carousel-wrapper{overflow:hidden;margin:-2rem -1.5rem;padding:2rem 1.5rem}.gpu-carousel-item img,img.gpu-carousel-item,.gpu-carousel-swipe-active{pointer-events:none}.gpu-carousel-single.gpu-carousel-fade{display:grid}.gpu-carousel-single.gpu-carousel-fade .gpu-carousel-item{width:100%;opacity:0;transition-property:opacity;transition-timing-function:ease-out;grid-column:1;grid-row:1}.gpu-carousel-single.gpu-carousel-fade .gpu-carousel-item.gpu-carousel-active{opacity:1}.gpu-carousel-single.gpu-carousel-slide{transition-timing-function:ease-out;transition-property:transform;position:relative;margin:0 auto;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.gpu-carousel-single.gpu-carousel-slide .gpu-carousel-item{flex-shrink:0;position:relative;transition-property:transform;width:100%}.gpu-carousel-numbered{transition-timing-function:ease-out;transition-property:transform;position:relative;margin:0 auto;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.gpu-carousel-numbered .gpu-carousel-item{flex-shrink:0;position:relative;transition-property:transform}.gpu-carousel-fixed-width{transition-timing-function:ease-out;transition-property:transform;position:relative;margin:0 auto;width:100%;height:100%;z-index:1;display:flex;box-sizing:content-box}.gpu-carousel-fixed-width .gpu-carousel-item{flex-shrink:0;position:relative;transition-property:transform}.gpu-autoplay-continuous{display:grid}.gpu-autoplay-continuous .gpu-carousel-item{grid-column:1;grid-row:1}.gpu-autoplay-continuous .gpu-carousel-item-animation{animation-duration:var(--duration, 10s);animation-delay:var(--delay, 1s);animation-timing-function:linear;animation-iteration-count:infinite;animation-name:slide}@keyframes slide{0%{transform:translate3d(var(--start-offset, 300px),0,0)}to{transform:translate3d(var(--end-offset, -200px),0,0)}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }], propDecorators: { items: [{
                type: ContentChildren,
                args: [GpuCarouselItemDirective, { descendants: true }]
            }], slider: [{
                type: ViewChild,
                args: ['slider']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], swipeActive: [{
                type: HostBinding,
                args: ['class.gpu-carousel-swipe-active']
            }], getHideOverflow: [{
                type: HostBinding,
                args: ['class.gpu-carousel-overflow-hidden']
            }], infinite: [{
                type: Input
            }], autoplay: [{
                type: Input
            }], autoplayContinuous: [{
                type: Input
            }], autoplayInterval: [{
                type: Input
            }], transitionDuration: [{
                type: Input
            }], itemGap: [{
                type: Input
            }], displayMode: [{
                type: Input
            }], swipeEnabled: [{
                type: Input
            }], tapEnabled: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], transitionAll: [{
                type: Input
            }], transition: [{
                type: Input
            }], pageChange: [{
                type: Output
            }] } });

var Theme;
(function (Theme) {
    Theme["LIGHT"] = "light";
    Theme["DARK"] = "dark";
    Theme["MIXED"] = "mixed";
})(Theme || (Theme = {}));
var Position;
(function (Position) {
    Position["OUTSIDE"] = "outside";
    Position["INSIDE"] = "inside";
    Position["MODAL"] = "modal";
})(Position || (Position = {}));
var ControlsSize;
(function (ControlsSize) {
    ControlsSize["NORMAL"] = "normal";
    ControlsSize["LARGE"] = "large";
    ControlsSize["EXTRA_LARGE"] = "extra_large";
})(ControlsSize || (ControlsSize = {}));
class GpuCarouselControlsComponent {
    constructor() {
        this.hasControls = true;
        this.theme = Theme.DARK;
        this.position = Position.OUTSIDE;
        this.controlsSize = ControlsSize.NORMAL;
    }
    get lightTheme() {
        return this.theme === Theme.LIGHT;
    }
    get darkTheme() {
        return this.theme === Theme.DARK;
    }
    get mixedTheme() {
        return this.theme === Theme.MIXED;
    }
    get positionInside() {
        return this.position === Position.INSIDE;
    }
    get positionModal() {
        return this.position === Position.MODAL;
    }
    get hasLargeControls() {
        return this.controlsSize === ControlsSize.LARGE;
    }
    get hasExtraLargeControls() {
        return this.controlsSize === ControlsSize.EXTRA_LARGE;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselControlsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuCarouselControlsComponent, selector: "gpu-carousel-controls", inputs: { theme: "theme", position: "position", controlsSize: "controlsSize" }, host: { properties: { "class.gpu-carousel-controls": "this.hasControls", "class.gpu-theme-light": "this.lightTheme", "class.gpu-theme-dark": "this.darkTheme", "class.gpu-theme-mixed": "this.mixedTheme", "class.gpu-position-inside": "this.positionInside", "class.gpu-position-modal": "this.positionModal", "class.gpu-large-controls": "this.hasLargeControls", "class.gpu-extra-large-controls": "this.hasExtraLargeControls" } }, ngImport: i0, template: "<ng-content></ng-content>\n", styles: [".gpu-carousel-controls{display:flex;flex-direction:row;justify-content:space-between}.gpu-carousel-controls.gpu-position-inside{z-index:1}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-dots{position:absolute;bottom:0;left:50%;transform:translate(-50%)}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-prev,.gpu-carousel-controls.gpu-position-inside .gpu-carousel-next{position:absolute;top:50%;transform:translateY(-50%)}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-prev{left:0}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-next{right:0}.gpu-carousel-controls.gpu-position-modal{z-index:1}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-dots{position:absolute;bottom:1rem;left:50%;transform:translate(-50%)}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-prev,.gpu-carousel-controls.gpu-position-modal .gpu-carousel-next{position:absolute;top:50%}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-prev{left:0;transform:translate(-100%,-50%)}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-next{right:0;transform:translate(100%,-50%)}@media (min-width: 768px){.gpu-carousel-controls.gpu-large-controls i.gpu-carousel-next,.gpu-carousel-controls.gpu-large-controls i.gpu-carousel-prev{height:4rem;width:4rem;font-size:4rem!important}}@media (min-width: 768px){.gpu-carousel-controls.gpu-extra-large-controls i.gpu-carousel-next,.gpu-carousel-controls.gpu-extra-large-controls i.gpu-carousel-prev{height:6rem;width:6rem;font-size:6rem!important}}gpu-carousel-dots:only-child{margin:auto}i.gpu-carousel-next,i.gpu-carousel-prev{height:2rem;width:2rem;font-size:2rem}i.gpu-carousel-next:before,i.gpu-carousel-prev:before{color:#c7c7d2}i.gpu-carousel-next:not(.gpu-disabled),i.gpu-carousel-prev:not(.gpu-disabled){cursor:pointer}i.gpu-carousel-next:hover,i.gpu-carousel-prev:hover{opacity:.7}i.gpu-carousel-next.gpu-disabled,i.gpu-carousel-prev.gpu-disabled{opacity:.3}.gpu-theme-light .gpu-carousel-next:before,.gpu-theme-light .gpu-carousel-prev:before,.gpu-theme-mixed .gpu-carousel-next:before,.gpu-theme-mixed .gpu-carousel-prev:before{color:#fff}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselControlsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-carousel-controls', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ng-content></ng-content>\n", styles: [".gpu-carousel-controls{display:flex;flex-direction:row;justify-content:space-between}.gpu-carousel-controls.gpu-position-inside{z-index:1}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-dots{position:absolute;bottom:0;left:50%;transform:translate(-50%)}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-prev,.gpu-carousel-controls.gpu-position-inside .gpu-carousel-next{position:absolute;top:50%;transform:translateY(-50%)}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-prev{left:0}.gpu-carousel-controls.gpu-position-inside .gpu-carousel-next{right:0}.gpu-carousel-controls.gpu-position-modal{z-index:1}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-dots{position:absolute;bottom:1rem;left:50%;transform:translate(-50%)}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-prev,.gpu-carousel-controls.gpu-position-modal .gpu-carousel-next{position:absolute;top:50%}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-prev{left:0;transform:translate(-100%,-50%)}.gpu-carousel-controls.gpu-position-modal .gpu-carousel-next{right:0;transform:translate(100%,-50%)}@media (min-width: 768px){.gpu-carousel-controls.gpu-large-controls i.gpu-carousel-next,.gpu-carousel-controls.gpu-large-controls i.gpu-carousel-prev{height:4rem;width:4rem;font-size:4rem!important}}@media (min-width: 768px){.gpu-carousel-controls.gpu-extra-large-controls i.gpu-carousel-next,.gpu-carousel-controls.gpu-extra-large-controls i.gpu-carousel-prev{height:6rem;width:6rem;font-size:6rem!important}}gpu-carousel-dots:only-child{margin:auto}i.gpu-carousel-next,i.gpu-carousel-prev{height:2rem;width:2rem;font-size:2rem}i.gpu-carousel-next:before,i.gpu-carousel-prev:before{color:#c7c7d2}i.gpu-carousel-next:not(.gpu-disabled),i.gpu-carousel-prev:not(.gpu-disabled){cursor:pointer}i.gpu-carousel-next:hover,i.gpu-carousel-prev:hover{opacity:.7}i.gpu-carousel-next.gpu-disabled,i.gpu-carousel-prev.gpu-disabled{opacity:.3}.gpu-theme-light .gpu-carousel-next:before,.gpu-theme-light .gpu-carousel-prev:before,.gpu-theme-mixed .gpu-carousel-next:before,.gpu-theme-mixed .gpu-carousel-prev:before{color:#fff}\n"] }]
        }], propDecorators: { hasControls: [{
                type: HostBinding,
                args: ['class.gpu-carousel-controls']
            }], lightTheme: [{
                type: HostBinding,
                args: ['class.gpu-theme-light']
            }], darkTheme: [{
                type: HostBinding,
                args: ['class.gpu-theme-dark']
            }], mixedTheme: [{
                type: HostBinding,
                args: ['class.gpu-theme-mixed']
            }], positionInside: [{
                type: HostBinding,
                args: ['class.gpu-position-inside']
            }], positionModal: [{
                type: HostBinding,
                args: ['class.gpu-position-modal']
            }], hasLargeControls: [{
                type: HostBinding,
                args: ['class.gpu-large-controls']
            }], hasExtraLargeControls: [{
                type: HostBinding,
                args: ['class.gpu-extra-large-controls']
            }], theme: [{
                type: Input
            }], position: [{
                type: Input
            }], controlsSize: [{
                type: Input
            }] } });

let GpuCarouselDotsComponent = class GpuCarouselDotsComponent {
    constructor(carousel, cdRef) {
        this.carousel = carousel;
        this.cdRef = cdRef;
        this.hostClass = 'gpu-carousel-dots';
        this.dots = [];
        this.activeIndex = 0;
    }
    ngAfterViewInit() {
        // It needs to wait for parent to finish initializing
        this.carousel.pageCount$.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((pageCount) => {
            this.dots = Array(pageCount);
            this.cdRef.detectChanges();
        });
        this.carousel.activePage$.pipe(untilDestroyed(this)).subscribe((index) => {
            this.activeIndex = index;
            this.cdRef.markForCheck();
        });
    }
    changePage(index) {
        this.carousel?.setPage(index);
        this.carousel.userInterrupted = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselDotsComponent, deps: [{ token: GPU_CAROUSEL }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuCarouselDotsComponent, selector: "gpu-carousel-dots", host: { properties: { "class": "this.hostClass" } }, ngImport: i0, template: "<span\n  *ngFor=\"let dot of dots; let i = index\"\n  class=\"gpu-carousel-bullet\"\n  tabindex=\"0\"\n  [class.gpu-carousel-bullet-active]=\"i === activeIndex\"\n  [attr.role]=\"i === activeIndex ? '' : 'button'\"\n  (click)=\"changePage(i)\"\n></span>\n", styles: [".gpu-carousel-dots{height:2rem}.gpu-carousel-bullet{width:8px;height:8px;display:inline-block;border-radius:50%;background:#c7c7d2;opacity:.25;transition:opacity .15s linear}.gpu-carousel-bullet.gpu-carousel-bullet-active{opacity:1}.gpu-carousel-bullet+.gpu-carousel-bullet{margin-left:.5rem}.gpu-theme-light .gpu-carousel-bullet{background:#fff}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
GpuCarouselDotsComponent = __decorate([
    UntilDestroy()
], GpuCarouselDotsComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselDotsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-carousel-dots', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<span\n  *ngFor=\"let dot of dots; let i = index\"\n  class=\"gpu-carousel-bullet\"\n  tabindex=\"0\"\n  [class.gpu-carousel-bullet-active]=\"i === activeIndex\"\n  [attr.role]=\"i === activeIndex ? '' : 'button'\"\n  (click)=\"changePage(i)\"\n></span>\n", styles: [".gpu-carousel-dots{height:2rem}.gpu-carousel-bullet{width:8px;height:8px;display:inline-block;border-radius:50%;background:#c7c7d2;opacity:.25;transition:opacity .15s linear}.gpu-carousel-bullet.gpu-carousel-bullet-active{opacity:1}.gpu-carousel-bullet+.gpu-carousel-bullet{margin-left:.5rem}.gpu-theme-light .gpu-carousel-bullet{background:#fff}\n"] }]
        }], ctorParameters: () => [{ type: GpuCarouselComponent, decorators: [{
                    type: Inject,
                    args: [GPU_CAROUSEL]
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { hostClass: [{
                type: HostBinding,
                args: ['class']
            }] } });

let GpuCarouselNextDirective = class GpuCarouselNextDirective {
    get disabled() {
        return this._disabled;
    }
    constructor(cdRef, carousel) {
        this.cdRef = cdRef;
        this.carousel = carousel;
        this.iconClass = true;
        this.itemClass = true;
        this.theme = 'dark';
        this._disabled = false;
        this.carousel.activePage$.pipe(untilDestroyed(this)).subscribe((page) => {
            this._disabled = page === this.carousel.pageCount - 1 && !this.carousel.infinite;
            this.cdRef.markForCheck();
        });
    }
    onClick() {
        if (this._disabled) {
            return;
        }
        this.carousel.next();
        this.carousel.userInterrupted = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselNextDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: GPU_CAROUSEL }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: GpuCarouselNextDirective, selector: "[gpuCarouselNext]", inputs: { theme: "theme" }, host: { listeners: { "click": "onClick()" }, properties: { "class.gpicon-forward": "this.iconClass", "class.gpu-carousel-next": "this.itemClass", "class.gpu-disabled": "this.disabled" } }, ngImport: i0 }); }
};
GpuCarouselNextDirective = __decorate([
    UntilDestroy()
], GpuCarouselNextDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselNextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[gpuCarouselNext]',
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: GpuCarouselComponent, decorators: [{
                    type: Inject,
                    args: [GPU_CAROUSEL]
                }] }], propDecorators: { iconClass: [{
                type: HostBinding,
                args: ['class.gpicon-forward']
            }], itemClass: [{
                type: HostBinding,
                args: ['class.gpu-carousel-next']
            }], disabled: [{
                type: HostBinding,
                args: ['class.gpu-disabled']
            }], theme: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

let GpuCarouselPrevDirective = class GpuCarouselPrevDirective {
    get disabled() {
        return this._disabled;
    }
    constructor(cdRef, carousel) {
        this.cdRef = cdRef;
        this.carousel = carousel;
        this.iconClass = true;
        this.itemClass = true;
        this.theme = 'dark';
        this._disabled = false;
        this.carousel.activePage$.pipe(untilDestroyed(this)).subscribe((page) => {
            this._disabled = page === 0 && !this.carousel.infinite;
            this.cdRef.markForCheck();
        });
    }
    onClick() {
        if (this._disabled) {
            return;
        }
        this.carousel.prev();
        this.carousel.userInterrupted = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselPrevDirective, deps: [{ token: i0.ChangeDetectorRef }, { token: GPU_CAROUSEL }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: GpuCarouselPrevDirective, selector: "[gpuCarouselPrev]", inputs: { theme: "theme" }, host: { listeners: { "click": "onClick()" }, properties: { "class.gpicon-back": "this.iconClass", "class.gpu-carousel-prev": "this.itemClass", "class.gpu-disabled": "this.disabled" } }, ngImport: i0 }); }
};
GpuCarouselPrevDirective = __decorate([
    UntilDestroy()
], GpuCarouselPrevDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselPrevDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[gpuCarouselPrev]',
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: GpuCarouselComponent, decorators: [{
                    type: Inject,
                    args: [GPU_CAROUSEL]
                }] }], propDecorators: { iconClass: [{
                type: HostBinding,
                args: ['class.gpicon-back']
            }], itemClass: [{
                type: HostBinding,
                args: ['class.gpu-carousel-prev']
            }], disabled: [{
                type: HostBinding,
                args: ['class.gpu-disabled']
            }], theme: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class HammerConfig extends HammerGestureConfig {
    constructor(_platformId) {
        super();
        this._platformId = _platformId;
        this.overrides = {
            swipe: { enable: true, direction: 6, threshold: 5 },
            pan: { enable: true, direction: 6, threshold: 5 },
            pinch: { enable: false },
            rotate: { enable: false },
        };
        if (isPlatformBrowser(this._platformId)) {
            this.getHammer();
        }
    }
    // hammerjs cannot be directly imported, it is using window class that crashes SSR build if imported/initialized on server
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getHammer() {
        if (window) {
            this.hammer = await import('hammerjs');
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: HammerConfig, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: HammerConfig }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: HammerConfig, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });

const components = [GpuCarouselComponent, GpuCarouselDotsComponent, GpuCarouselControlsComponent];
const directives = [GpuCarouselItemDirective, GpuCarouselPrevDirective, GpuCarouselNextDirective];
class GpuCarouselModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselModule, declarations: [GpuCarouselComponent, GpuCarouselDotsComponent, GpuCarouselControlsComponent, GpuCarouselItemDirective, GpuCarouselPrevDirective, GpuCarouselNextDirective], imports: [CommonModule, HammerModule], exports: [GpuCarouselComponent, GpuCarouselDotsComponent, GpuCarouselControlsComponent, GpuCarouselItemDirective, GpuCarouselPrevDirective, GpuCarouselNextDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselModule, providers: [
            {
                provide: HAMMER_GESTURE_CONFIG,
                useClass: HammerConfig,
            },
        ], imports: [CommonModule, HammerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuCarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...components, ...directives],
                    imports: [CommonModule, HammerModule],
                    exports: [...components, ...directives],
                    providers: [
                        {
                            provide: HAMMER_GESTURE_CONFIG,
                            useClass: HammerConfig,
                        },
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GpuCarouselComponent, GpuCarouselControlsComponent, GpuCarouselDotsComponent, GpuCarouselItemDirective, GpuCarouselModule, GpuCarouselNextDirective, GpuCarouselPrevDirective };
